/**
 * Profile microsite analytics config (Didomi CMP + GA4).
 * Aligned with OEServer didomi-config.js / analytics-config.js (profile notice bMGE6xxH).
 * Optional REACT_APP_* env vars override constants for local dev only.
 */

/** Didomi organization public API key (shared with OEServer). */
export const PROFILE_DIDOMI_API_KEY = '6263f82c-f549-4a68-b0a6-fbbfc646681d';

/** Profile microsite Didomi notice (not the app login notice QqL9w2bF). */
export const PROFILE_DIDOMI_NOTICE_ID = 'yFwnfyWN';

export const PROFILE_DIDOMI_ANALYTICS_PURPOSE_ID = 'cookies';

/** Didomi CMP vendor id for GA4 — not the GA4 measurement id. */
export const PROFILE_DIDOMI_ANALYTICS_VENDOR_ID = 'googleana-4TXnJigR';

/** Didomi custom vendor id for PostHog (same as OEServer didomi-config.js). */
export const PROFILE_DIDOMI_POSTHOG_VENDOR_ID = 'posthog-nQeTQfC6';

export const PROFILE_DIDOMI_PRIVACY_POLICY_URL = 'https://www.outboundengine.com/privacy-policy/';

export const PROFILE_DIDOMI_GDPR_APPLIES_GLOBALLY = false;
export const PROFILE_DIDOMI_EMBED_TCF_STUB = false;
export const PROFILE_DIDOMI_EMBED_GPP_STUB = true;

export const PROFILE_DIDOMI_COUNTRY = null;
export const PROFILE_DIDOMI_REGION = null;

/** GA4 measurement id for profile microsites. */
export const PROFILE_GA4_MEASUREMENT_ID = 'G-EN5QMKPLJ4';

function envFlag(name, defaultValue) {
  const value = process.env[name];
  if (value === undefined) return defaultValue;
  return value === 'true' || value === '1';
}

function blankToNull(value) {
  if (value === undefined || value === null) return null;
  const trimmed = String(value).trim();
  return trimmed.length ? trimmed : null;
}

function analyticsFromConstants() {
  return {
    didomi: {
      apiKey: PROFILE_DIDOMI_API_KEY,
      noticeId: PROFILE_DIDOMI_NOTICE_ID,
      analyticsPurposeId: PROFILE_DIDOMI_ANALYTICS_PURPOSE_ID,
      analyticsVendorId: PROFILE_DIDOMI_ANALYTICS_VENDOR_ID,
      gdprAppliesGlobally: PROFILE_DIDOMI_GDPR_APPLIES_GLOBALLY,
      embedTcfStub: PROFILE_DIDOMI_EMBED_TCF_STUB,
      embedGppStub: PROFILE_DIDOMI_EMBED_GPP_STUB,
      country: PROFILE_DIDOMI_COUNTRY,
      region: PROFILE_DIDOMI_REGION,
      privacyPolicyUrl: PROFILE_DIDOMI_PRIVACY_POLICY_URL,
    },
    ga4: {
      measurementIdProfile: PROFILE_GA4_MEASUREMENT_ID,
    },
  };
}

function analyticsFromEnv() {
  const hasEnv = process.env.REACT_APP_DIDOMI_NOTICE_ID
    || process.env.REACT_APP_GA4_MEASUREMENT_ID_PROFILE;
  if (!hasEnv) return null;

  return {
    didomi: {
      apiKey: PROFILE_DIDOMI_API_KEY,
      noticeId: process.env.REACT_APP_DIDOMI_NOTICE_ID || PROFILE_DIDOMI_NOTICE_ID,
      analyticsPurposeId: process.env.REACT_APP_DIDOMI_ANALYTICS_PURPOSE_ID
        || PROFILE_DIDOMI_ANALYTICS_PURPOSE_ID,
      analyticsVendorId: process.env.REACT_APP_DIDOMI_ANALYTICS_VENDOR_ID
        || PROFILE_DIDOMI_ANALYTICS_VENDOR_ID,
      gdprAppliesGlobally: envFlag(
        'REACT_APP_DIDOMI_GDPR_APPLIES_GLOBALLY',
        PROFILE_DIDOMI_GDPR_APPLIES_GLOBALLY,
      ),
      embedTcfStub: envFlag(
        'REACT_APP_DIDOMI_EMBED_TCF_STUB',
        PROFILE_DIDOMI_EMBED_TCF_STUB,
      ),
      embedGppStub: envFlag(
        'REACT_APP_DIDOMI_EMBED_GPP_STUB',
        PROFILE_DIDOMI_EMBED_GPP_STUB,
      ),
      country: blankToNull(process.env.REACT_APP_DIDOMI_COUNTRY),
      region: blankToNull(process.env.REACT_APP_DIDOMI_REGION),
      privacyPolicyUrl: process.env.REACT_APP_DIDOMI_PRIVACY_POLICY_URL
        || PROFILE_DIDOMI_PRIVACY_POLICY_URL,
    },
    ga4: {
      measurementIdProfile: process.env.REACT_APP_GA4_MEASUREMENT_ID_PROFILE
        || PROFILE_GA4_MEASUREMENT_ID,
    },
  };
}

/**
 * @returns {ReturnType<typeof analyticsFromConstants>}
 */
export function getProfileAnalytics() {
  return analyticsFromEnv() || analyticsFromConstants();
}

/** Primary GA4 Didomi vendor only (Sencha parity — do not OR generic `google`). */
export function getDidomiAnalyticsVendorIds() {
  return [getProfileAnalytics().didomi.analyticsVendorId || PROFILE_DIDOMI_ANALYTICS_VENDOR_ID];
}

export function getPrimaryAnalyticsVendorId() {
  return getDidomiAnalyticsVendorIds()[0];
}

export function getDidomiAnalyticsPurposeId() {
  return getProfileAnalytics().didomi.analyticsPurposeId || PROFILE_DIDOMI_ANALYTICS_PURPOSE_ID;
}

export function getDidomiPostHogVendorIds() {
  return [PROFILE_DIDOMI_POSTHOG_VENDOR_ID];
}

export function getGa4MeasurementIdProfile() {
  return getProfileAnalytics().ga4.measurementIdProfile || PROFILE_GA4_MEASUREMENT_ID;
}
